/**
 * Решения шлюза ссылки на занятие (`lesson-link-gate.tsx`), вынесенные чистыми
 * функциями ради тестов: бегунок `@edu/web` видит только `*.test.ts`, а
 * компоненты мы не тестируем (раздел 9 CLAUDE.md).
 *
 * ⚠️ Файл едет в СТАРТОВОМ чанке вместе со шлюзом, поэтому здесь нет ни одного
 * импорта: только строки и числа.
 */

/**
 * Что делать после пробы урока правами текущей сессии.
 *
 * - урок открылся (`ok`) или ответил чем угодно, кроме «нет доступа», входим:
 *   это «урок мой», а ранний приход и прочие отказы страница урока разберёт
 *   своими словами;
 * - 404 это урок чужой для этой сессии, 401 это сессия протухла. В обоих
 *   случаях гасим ключ: сервер сам ответит, впустить ли анонима (200), чужая ли
 *   живая сессия при верном ключе (409) или ключ не годится (404).
 *
 * ⚠️ 401 без этой ветки уводил бы в страницу урока, а та при протухшей сессии
 * показывает экран «сначала войдите», то есть ровно тот тупик, от которого
 * ссылка и заведена.
 */
export function afterLessonProbe(result: "ok" | { status: number | null }): "enter" | "redeem" {
  if (result === "ok") return "enter";
  return result.status === 404 || result.status === 401 ? "redeem" : "enter";
}

/**
 * Экран отказа: абзацы текста и нужна ли тихая кнопка «Повторить».
 *
 * `code` это код отказа API, `signout` это неудавшийся выход из чужой сессии,
 * `null` это отказ без кода (сеть, таймаут, неразобранный ответ).
 *
 * ⚠️ **Отказ ключа НЕ говорит «устарела».** Сервер отвечает одним и тем же
 * `lesson_link_not_found` и на прошедшее занятие, и на занятие, до которого ещё
 * больше суток (различать их снаружи значит подсказывать перебирающему), а
 * ученик, открывший ссылку накануне, должен понять, что она заработает позже,
 * а не просить новую. Поэтому текст нейтральный и называет оба случая.
 *
 * «Повторить» стоит только там, где повтор может помочь, то есть где в тексте
 * и сказано «попробуйте ещё раз». Отказ ключа, блокировку и лимит частоты
 * повтор не лечит, а при лимите вредит.
 */
export function linkFailure(code: string | "signout" | null): { text: string[]; retry: boolean } {
  switch (code) {
    case "lesson_link_not_found":
      return {
        text: [
          "Эта ссылка сейчас не работает. Она действует только около времени занятия и перестаёт, когда занятие завершено.",
          "Если занятие ещё не скоро, откройте ссылку ближе к началу. Если оно уже идёт, попросите у преподавателя новую ссылку.",
        ],
        retry: false,
      };
    case "rate_limited":
      return { text: ["Слишком много попыток. Подождите час и откройте ссылку снова."], retry: false };
    /*
      ⚠️ Кто заблокирован, ученик или преподаватель, не называется: сервер этого
      не отдаёт (разбор у `redeemInvite`), а «ваш преподаватель заблокирован»
      это сведения о третьем лице.
    */
    case "account_blocked":
      return {
        text: ["Вход по этой ссылке закрыт: доступ к занятиям заблокирован. Если это ошибка, напишите преподавателю."],
        retry: false,
      };
    case "signout":
      return {
        text: ["Не получилось выйти из текущего входа. Проверьте интернет и попробуйте ещё раз."],
        retry: true,
      };
    default:
      return {
        text: ["Войти по ссылке не получилось. Проверьте интернет и попробуйте ещё раз."],
        retry: true,
      };
  }
}
